import {
  createContext,
  useState,
  useContext,
  ReactElement,
  PropsWithChildren,
  useEffect,
} from 'react';

import {useDebounce} from '~/hooks';
import {delay} from '~/utils';

interface TFRContext {
  searchPostcode: string;
  setSearchPostcode: (v: string) => void;
  isSearching: boolean;
}

export const AppContext = createContext<TFRContext>({
  searchPostcode: '',
  setSearchPostcode: (v: string) => v,
  isSearching: false,
});

export const AppContextProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  const [searchPostcode, setSearchPostcode] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const onSearch = useDebounce(async () => {
    setIsSearching(true);
    await delay(200);
    setIsSearching(false);
  }, 500);

  useEffect(() => {
    onSearch(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPostcode]);

  useEffect(() => {
    if (localStorage) {
      const p = localStorage?.getItem('postalCode');
      if (p) {
        setSearchPostcode(p);
      }
    }
  }, []);

  const _setSearchPostcode = (_postalCode: string) => {
    setSearchPostcode(_postalCode);
    if (localStorage) {
      localStorage?.setItem('postalCode', _postalCode);
    }
  };

  return (
    <AppContext.Provider
      value={{
        searchPostcode,
        setSearchPostcode: _setSearchPostcode,
        isSearching,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): TFRContext => useContext(AppContext);
